import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, MapPin, Navigation, ArrowRight, Zap, PhoneCall, Star,
  ShieldCheck, CheckCircle2, XCircle, ChevronRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import Icon from "../components/Icon";
import ServiceCard from "../components/ServiceCard";
import SeoHead from "../components/SeoHead";
import { useServices } from "../context/ServicesContext";
import { LOCALITIES } from "../data/services";
import {
  STEPS, FEATURES, STATS, TESTIMONIALS, FAQS, PARTNER_BENEFITS,
  WHY_COMPARISON, LIVE_FEED, HERO_IMAGE, whatsappLink,
} from "../data/content";

function SectionHead({ eyebrow, title, desc, center }) {
  return (
    <div className={`${center ? "text-center mx-auto" : ""} max-w-2xl mb-10`}>
      {eyebrow && <p className="text-xs uppercase tracking-widest font-bold text-brand-orange mb-2">{eyebrow}</p>}
      <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">{title}</h2>
      {desc && <p className="mt-3 text-base sm:text-lg text-slate-600 leading-relaxed">{desc}</p>}
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { services, loading } = useServices();
  const [query, setQuery] = useState("");
  const [loc, setLoc] = useState("");

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (loc) params.set("locality", loc);
    navigate(`/book?${params.toString()}`);
  };

  return (
    <div>
      <SeoHead
        title="ApnaFix - Har Local Problem Ka Solution | Local Home Services Mumbai"
        description="Book trusted local AC repair, electrician, plumber, carpenter & home services near you in Kandivali, Malad, Borivali & Goregaon."
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-navy">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "28px 28px" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-10 lg:pt-20 lg:pb-16 grid lg:grid-cols-2 gap-10 items-center">
          <div className="af-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-xs font-semibold text-orange-200">
              <ShieldCheck className="w-3.5 h-3.5" /> Verified local professionals near you
            </span>
            <h1 className="mt-5 font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight text-white">
              Har Local Problem<br /><span className="text-brand-orange">Ka Solution.</span>
            </h1>
            <p className="mt-4 text-base sm:text-lg text-slate-300 max-w-lg leading-relaxed">
              AC repair, electrician, plumber, carpenter and more — find trusted local professionals near you.
            </p>

            {/* Search card */}
            <div className="mt-7 bg-white rounded-2xl p-3 sm:p-4 shadow-2xl shadow-black/20 grid sm:grid-cols-[1fr_1fr_auto] gap-2.5">
              <div className="relative">
                <label className="sr-only">What service do you need?</label>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  data-testid="hero-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="AC repair, electrician, plumber..."
                  className="w-full h-12 pl-9 pr-3 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-orange"
                />
              </div>
              <div className="relative">
                <Select value={loc} onValueChange={setLoc}>
                  <SelectTrigger data-testid="hero-location-dropdown" className="h-12 rounded-xl bg-slate-50 border-slate-200 text-sm">
                    <span className="inline-flex items-center gap-2 text-slate-600"><MapPin className="w-4 h-4 text-brand-orange" /><SelectValue placeholder="Your location" /></span>
                  </SelectTrigger>
                  <SelectContent>
                    {LOCALITIES.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button
                data-testid="hero-find-pro-submit"
                onClick={handleSearch}
                className="h-12 px-5 rounded-xl bg-brand-orange hover:bg-orange-600 text-white font-semibold active:scale-[0.98] transition-transform"
              >
                Find a Pro <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button onClick={() => setLoc(LOCALITIES[0].name)} className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-300 hover:text-white">
                <Navigation className="w-3.5 h-3.5 text-brand-orange" /> Use current location
              </button>
              <Button variant="ghost" data-testid="hero-partner-cta" onClick={() => navigate("/partner-register")} className="text-orange-200 hover:text-white hover:bg-white/10 h-8 text-sm">
                Become a Service Partner <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="relative af-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
              <img src={HERO_IMAGE} alt="ApnaFix verified local technician at a Mumbai home" className="w-full h-[320px] sm:h-[440px] object-cover" loading="eager" />
            </div>
            <div className="absolute -bottom-5 left-4 bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600"><ShieldCheck className="w-5 h-5" /></span>
              <div>
                <p className="font-mono-num font-bold text-slate-900 leading-none">4.8/5</p>
                <p className="text-xs text-slate-500 mt-0.5">Verified pros rating</p>
              </div>
            </div>
            <div className="absolute -top-3 right-3 bg-brand-orange text-white rounded-2xl shadow-xl px-4 py-3 hidden sm:flex items-center gap-2 animate-pulse">
              <Zap className="w-5 h-5" />
              <div className="leading-none">
                <p className="font-mono-num font-bold">20 min</p>
                <p className="text-[11px] opacity-90 mt-0.5">avg. arrival</p>
              </div>
            </div>
          </div>
        </div>

        {/* Live feed marquee */}
        <div className="relative border-t border-white/10 bg-navy-900/40 overflow-hidden py-2.5">
          <div className="flex gap-10 whitespace-nowrap af-marquee w-max">
            {[...LIVE_FEED, ...LIVE_FEED].map((t, i) => (
              <span key={i} className="inline-flex items-center gap-2 text-xs text-slate-300">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-7 grid grid-cols-2 md:grid-cols-4 gap-4">
          {STATS.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-center">
              <p className="font-mono-num text-2xl sm:text-3xl font-bold text-navy">{s.value}</p>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-[11px] text-slate-400 pb-3">* Sample figures shown for launch — to be replaced with live numbers.</p>
      </section>

      {/* POPULAR SERVICES */}
      <section id="services" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 scroll-mt-20">
        <SectionHead eyebrow="Popular Services" title="What do you need help with?" desc={`Choose from ${services.length || 15}+ trusted local services. Verified professionals, transparent pricing.`} />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {loading && services.length === 0
            ? Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-48 rounded-2xl bg-slate-100 animate-pulse" />)
            : services.map((s, i) => <ServiceCard key={s.id} service={s} index={i} />)}
        </div>
      </section>

      {/* EMERGENCY SOS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-orange-600 via-amber-600 to-orange-700 text-white p-7 sm:p-10 shadow-lg shadow-orange-500/20">
          <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <span data-testid="hero-emergency-sos-badge" className="inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-xs font-bold uppercase tracking-wide">
                <Zap className="w-3.5 h-3.5" /> Emergency / Quick Service
              </span>
              <h2 className="mt-3 font-display text-2xl sm:text-3xl font-extrabold">Need help right now?</h2>
              <p className="mt-2 text-white/90 max-w-lg">Geyser short-circuit? Pipe burst? Get connected with an available local professional nearby.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button data-testid="emergency-banner-find-btn" onClick={() => navigate("/book")} className="bg-white text-orange-700 hover:bg-orange-50 font-bold h-12 px-6">
                Find Someone Nearby
              </Button>
              <a href={whatsappLink("Emergency! I need a local professional right now.")} target="_blank" rel="noreferrer"
                 data-testid="emergency-banner-call-btn"
                 className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/60 text-white font-bold h-12 px-6 hover:bg-white/10">
                <PhoneCall className="w-4 h-4" /> WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 scroll-mt-20">
        <SectionHead center eyebrow="How ApnaFix Works" title="Fixed in 4 simple steps" desc="From problem to solution — fast, local and reliable." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((s, i) => (
            <div key={s.n} data-testid={`how-it-works-step-${s.n}`} className="relative bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg transition-shadow af-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              <span className="font-mono-num text-5xl font-bold text-slate-100 absolute top-3 right-4">{s.n}</span>
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-navy text-white"><Icon name={s.icon} className="w-6 h-6" /></span>
              <h3 className="mt-4 font-display font-bold text-slate-900">{s.title}</h3>
              <p className="mt-1 text-sm text-slate-600">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHY APNAFIX */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <SectionHead center eyebrow="Why ApnaFix?" title="Local professionals. Simple booking. Reliable service." />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {FEATURES.map((f, i) => (
              <div key={f.title} data-testid="why-apnafix-guarantee-card" className="rounded-2xl border border-slate-200 p-5 hover:border-orange-300 hover:-translate-y-1 transition-all af-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-orange-50 text-brand-orange"><Icon name={f.icon} className="w-5 h-5" /></span>
                <h3 className="mt-3 font-display font-bold text-slate-900 text-sm sm:text-base">{f.title}</h3>
                <p className="mt-1 text-xs sm:text-sm text-slate-600">{f.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 max-w-3xl mx-auto rounded-2xl border border-slate-200 overflow-hidden">
            <div className="grid grid-cols-[1fr_auto_auto] bg-slate-50 text-xs sm:text-sm font-semibold text-slate-600 px-4 sm:px-6 py-3">
              <span>ApnaFix vs unverified local hustlers</span>
              <span className="px-3 text-navy">ApnaFix</span>
              <span className="px-3 text-slate-400">Others</span>
            </div>
            {WHY_COMPARISON.map((r) => (
              <div key={r.point} className="grid grid-cols-[1fr_auto_auto] items-center px-4 sm:px-6 py-3 border-t border-slate-100 text-sm">
                <span className="text-slate-700">{r.point}</span>
                <span className="px-3 grid place-items-center"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></span>
                <span className="px-3 grid place-items-center"><XCircle className="w-5 h-5 text-slate-300" /></span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <SectionHead eyebrow="Service Areas" title="ApnaFix Near You" desc="Now serving these Mumbai neighbourhoods — expanding fast." />
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6 items-stretch">
          <div className="relative rounded-3xl overflow-hidden border border-slate-200 bg-navy min-h-[280px]">
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "22px 22px" }} />
            <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
            {[
              { l: "Kandivali", top: "30%", left: "28%" },
              { l: "Malad", top: "55%", left: "45%" },
              { l: "Borivali", top: "18%", left: "62%" },
              { l: "Goregaon", top: "70%", left: "68%" },
            ].map((p) => (
              <div key={p.l} className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" style={{ top: p.top, left: p.left }}>
                <span className="grid place-items-center w-8 h-8 rounded-full bg-brand-orange text-white shadow-lg animate-pulse"><MapPin className="w-4 h-4" /></span>
                <span className="mt-1 text-[11px] font-semibold text-white bg-black/30 px-2 py-0.5 rounded-full">{p.l}</span>
              </div>
            ))}
            <div className="absolute bottom-4 left-4 text-white">
              <p className="text-xs uppercase tracking-widest font-bold text-orange-300">Launch Area</p>
              <p className="font-display font-bold">Kandivali • Malad • Borivali • Goregaon</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {LOCALITIES.map((l) => (
              <button key={l.id} data-testid={`locality-pill-${l.slug}`} onClick={() => navigate(`/locations/${l.slug}`)}
                      className="text-left bg-white rounded-2xl border border-slate-200 p-4 hover:border-orange-400 hover:shadow-md transition-all">
                <span className="grid place-items-center w-9 h-9 rounded-lg bg-orange-50 text-brand-orange"><MapPin className="w-4 h-4" /></span>
                <h3 className="mt-2 font-display font-bold text-slate-900">{l.name}</h3>
                <p className="text-xs text-slate-500">{l.societies}</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER RECRUITMENT */}
      <section className="bg-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-widest font-bold text-orange-300 mb-2">For Service Partners</p>
            <h2 className="font-display text-2xl sm:text-4xl font-extrabold tracking-tight">Grow Your Business With ApnaFix</h2>
            <p className="mt-3 text-slate-300 max-w-lg">Get more local customers, manage your jobs and grow your earnings — all from one platform.</p>
            <Button data-testid="partner-section-join-btn" onClick={() => navigate("/partner-register")} className="mt-6 bg-brand-orange hover:bg-orange-600 text-white font-bold h-12 px-6">
              Join ApnaFix as a Partner <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {PARTNER_BENEFITS.map((b) => (
              <div key={b.title} className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-4">
                <span className="grid place-items-center w-10 h-10 rounded-xl bg-brand-orange/20 text-orange-300"><Icon name={b.icon} className="w-5 h-5" /></span>
                <span className="text-sm font-medium">{b.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <SectionHead eyebrow="Customer Reviews" title="Built around your local community" desc="Sample stories shown until verified reviews go live." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TESTIMONIALS.map((t, i) => (
            <div key={t.name} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-lg transition-shadow af-fade-up" style={{ animationDelay: `${i * 50}ms` }}>
              <div className="flex gap-0.5 text-amber-400 mb-3">
                {Array.from({ length: 5 }).map((_, j) => <Star key={j} className={`w-4 h-4 ${j < t.rating ? "fill-current" : "text-slate-200"}`} />)}
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">"{t.text}"</p>
              <div className="mt-4 flex items-center gap-3">
                <span className="grid place-items-center w-9 h-9 rounded-full bg-navy text-white font-display font-bold text-sm">{t.name.charAt(0)}</span>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-500">Customer, {t.area}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
          <SectionHead center eyebrow="FAQ" title="Questions? We've got answers" />
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-display font-semibold text-slate-900 hover:text-brand-orange">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 text-sm leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="rounded-3xl bg-navy text-white text-center px-6 py-14 relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "26px 26px" }} />
          <div className="relative">
            <h2 className="font-display text-2xl sm:text-4xl font-extrabold tracking-tight">Your Local Problem. Our Local Solution.</h2>
            <p className="mt-3 text-slate-300">Book a trusted professional today.</p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <Button data-testid="final-cta-book" onClick={() => navigate("/book")} className="bg-brand-orange hover:bg-orange-600 text-white font-bold h-12 px-8">Book a Service</Button>
              <Button data-testid="final-cta-partner" variant="outline" onClick={() => navigate("/partner-register")} className="border-white/30 bg-transparent text-white hover:bg-white/10 font-bold h-12 px-8">Become a Partner</Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
